/**
 * Saved BA discovery presets — persisted in localStorage (per browser).
 * Lets admins reuse keyword/location combos without retyping.
 */

export interface DiscoveryPreset {
  id: string;
  name: string;
  keywordsCSV: string;
  location: string;
  sinceDays: number;
  maxPages: number;
  previewKeyword: string;
  createdAt: number;
}

const STORAGE_KEY = 'b2b_discovery_presets_v1';

function readAll(): DiscoveryPreset[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as DiscoveryPreset[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeAll(presets: DiscoveryPreset[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(presets));
}

export function listDiscoveryPresets(): DiscoveryPreset[] {
  return readAll().sort((a, b) => b.createdAt - a.createdAt);
}

export function saveDiscoveryPreset(
  preset: Omit<DiscoveryPreset, 'id' | 'createdAt'>,
): DiscoveryPreset {
  const entry: DiscoveryPreset = {
    ...preset,
    id: crypto.randomUUID(),
    createdAt: Date.now(),
  };
  writeAll([entry, ...readAll()]);
  return entry;
}

export function deleteDiscoveryPreset(id: string): void {
  writeAll(readAll().filter((p) => p.id !== id));
}

/** Built-in starter presets for common DACH IT searches. */
export const DEFAULT_DISCOVERY_PRESETS: Omit<DiscoveryPreset, 'id' | 'createdAt'>[] = [
  {
    name: 'DE · Software Engineer (14d)',
    keywordsCSV: 'Software Engineer, Backend Developer, Full Stack Developer',
    location: 'Deutschland',
    sinceDays: 14,
    maxPages: 3,
    previewKeyword: 'Software Engineer',
  },
  {
    name: 'Berlin · Backend (14d)',
    keywordsCSV: 'Backend Developer, Software Engineer, DevOps Engineer',
    location: 'Berlin',
    sinceDays: 14,
    maxPages: 3,
    previewKeyword: 'Backend Developer',
  },
  {
    name: 'München · Data/ML (30d)',
    keywordsCSV: 'Data Engineer, Machine Learning Engineer, Data Scientist',
    location: 'München',
    sinceDays: 30,
    maxPages: 2,
    previewKeyword: 'Data Engineer',
  },
];
